import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Check,
  Download,
  FileSpreadsheet,
  PenSquare,
  Plus,
  ShieldAlert,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { read, utils, write } from "xlsx";
import {
  getPlatformAccess,
  listPlatformTenants,
  getPlatformTenantDetail,
  createPlatformTenant,
  updatePlatformTenant,
  setPlatformTenantStatus,
  setPlatformTenantDomains,
  listPlatformTenantEmails,
  setPlatformTenantEmails,


  type PlatformTenantRow,
  type PlatformTenantDetail,
} from "@/lib/platform/tenants.functions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

/**
 * Platform-only Tenant Management.
 *
 * Layer 1: /_admin gate (admin authentication).
 * Layer 2: server-side `platform_is_owner()` — enforced inside every RPC; the
 *          client checks below are UX only.
 */
export const Route = createFileRoute("/_admin/admin/tenants")({
  ssr: false,
  component: TenantManagementPage,
});

const STATUSES = ["active", "inactive", "pending"] as const;

function fmt(value?: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
}

/** Map raw server/RPC errors to a clean user-facing message. */
function friendlyError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  if (/No authorization header|Unauthorized|Invalid token/i.test(raw))
    return "Your session has expired. Please sign in again.";
  if (/forbidden/i.test(raw)) return "You are not authorized to do this.";
  if (/duplicate_code/i.test(raw)) return "That tenant code is already in use.";
  if (/duplicate_domain:(.*)/i.test(raw))
    return `Duplicate domain: ${raw.split("duplicate_domain:")[1]}`;
  if (/domain_taken:(.*)/i.test(raw))
    return `This domain is already assigned to another tenant: ${raw.split("domain_taken:")[1]}`;
  if (/invalid_domain:(.*)/i.test(raw))
    return `Invalid domain format: ${raw.split("invalid_domain:")[1]}`;
  if (/domain_required/i.test(raw)) return "Domain is required.";
  if (/primary_domain_required/i.test(raw))
    return "Exactly one domain must be marked as primary.";
  if (/duplicate_email:(.*)/i.test(raw))
    return `Duplicate email: ${raw.split("duplicate_email:")[1]}`;
  if (/invalid_email:(.*)/i.test(raw))
    return `Invalid email format: ${raw.split("invalid_email:")[1]}`;
  if (/email_required/i.test(raw)) return "Email address is required.";



  if (/invalid_code/i.test(raw))
    return "Tenant code must be 3–32 characters: A–Z, 0–9 or underscore.";
  if (/invalid_status/i.test(raw)) return "Invalid tenant status.";
  if (/name_required/i.test(raw)) return "Tenant name is required.";
  if (/not_found/i.test(raw)) return "Tenant not found.";
  if (/fetch|network/i.test(raw)) return "Network error. Please try again.";
  return "Something went wrong. Please try again.";
}

function isAuthError(err: unknown) {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  return /No authorization header|Unauthorized|Invalid token|No request headers/i.test(raw);
}

function downloadBook(sheets: Array<{ name: string; rows: Array<Record<string, unknown>> }>, filename: string) {
  const wb = utils.book_new();
  for (const s of sheets) {
    utils.book_append_sheet(wb, utils.json_to_sheet(s.rows.length ? s.rows : [{}]), s.name);
  }
  const out = write(wb, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
  const url = URL.createObjectURL(
    new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type ImportRow = {
  row: number;
  name: string;
  code: string;
  status: string;
  domains: Array<{ host: string; is_primary: boolean }>;
  problem: string | null;
};

function TenantManagementPage() {
  const queryClient = useQueryClient();
  const access = useServerFn(getPlatformAccess);
  const listFn = useServerFn(listPlatformTenants);
  const detailFn = useServerFn(getPlatformTenantDetail);
  const createFn = useServerFn(createPlatformTenant);
  const updateFn = useServerFn(updatePlatformTenant);
  const statusFn = useServerFn(setPlatformTenantStatus);
  const domainsFn = useServerFn(setPlatformTenantDomains);
  const listEmailsFn = useServerFn(listPlatformTenantEmails);
  const emailsFn = useServerFn(setPlatformTenantEmails);



  const [viewId, setViewId] = useState<string | null>(null);
  const [editTenant, setEditTenant] = useState<PlatformTenantRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState<PlatformTenantRow | null>(null);
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Authorization state. The server fn throws when no bearer token is present
  // (logged out); that is surfaced as a clean state, never a blank screen.
  const accessQuery = useQuery({
    queryKey: ["platform", "access", "tenants-page"],
    retry: false,
    queryFn: async () => {
      try {
        const r = await access();
        return { isPlatformOwner: r.isPlatformOwner === true, signedOut: false };
      } catch (err) {
        if (isAuthError(err)) return { isPlatformOwner: false, signedOut: true };
        return { isPlatformOwner: false, signedOut: false };
      }
    },
  });
  const isOwner = accessQuery.data?.isPlatformOwner === true;

  const tenantsQuery = useQuery({
    queryKey: ["platform", "tenants"],
    queryFn: () => listFn(),
    enabled: isOwner,
    retry: false,
  });

  const detailQuery = useQuery({
    queryKey: ["platform", "tenant", viewId],
    queryFn: () => detailFn({ data: { tenantId: viewId! } }),
    enabled: isOwner && !!viewId,
    retry: false,
  });

  const editDetailQuery = useQuery({
    queryKey: ["platform", "tenant", editTenant?.id],
    queryFn: () => detailFn({ data: { tenantId: editTenant!.id } }),
    enabled: isOwner && !!editTenant,
    retry: false,
  });

  const editEmailsQuery = useQuery({
    queryKey: ["platform", "tenant-emails", editTenant?.id],
    queryFn: () => listEmailsFn({ data: { tenantId: editTenant!.id } }),
    enabled: isOwner && !!editTenant,
    retry: false,
  });





  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["platform", "tenants"] });

  async function exportExcel() {
    try {
      setBusy(true);
      const details = await Promise.all(
        tenants.map((t) =>
          detailFn({ data: { tenantId: t.id } }).catch(() => null),
        ),
      );
      const users: Array<Record<string, unknown>> = [];
      const domains: Array<Record<string, unknown>> = [];
      details.forEach((d, i) => {
        const t = tenants[i]!;
        (d?.users ?? []).forEach((u) =>
          users.push({
            "Tenant Code": t.code,
            "Tenant Name": t.name,
            User: u.email ?? u.user_id,
            Roles: u.roles.join(", "),
            "Member Since": fmt(u.joined_at),
            "Last Sign-in": fmt(u.last_sign_in_at),
          }),
        );
        (d?.domains ?? []).forEach((dm) =>
          domains.push({
            "Tenant ID": t.id,
            "Tenant Code": t.code,
            "Tenant Name": t.name,
            Host: dm.host,
            Primary: dm.is_primary ? "Yes" : "No",
            Added: fmt(dm.created_at),
          }),
        );
      });
      downloadBook(
        [
          {
            name: "Tenants",
            rows: tenants.map((t) => ({
              "Tenant ID": t.id,
              "Tenant Name": t.name,
              "Tenant Code": t.code,
              Status: t.status,
              Users: t.member_count,
              Domains: t.domain_count,
              Created: fmt(t.created_at),
              Updated: fmt(t.updated_at),
            })),
          },
          { name: "Tenant Users", rows: users },
          { name: "Tenant Domains", rows: domains },
        ],
        "tenant-management-export.xlsx",
      );
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setBusy(false);
    }
  }

  function downloadTemplate() {
    downloadBook(
      [
        {
          name: "Tenants",
          rows: [
            {
              "Tenant Name": "Example Kitchen",
              "Tenant Code": "TENANT_EXAMPLE",
              Status: "pending",
            },
          ],
        },
        {
          name: "Tenant Domains",
          rows: [
            { "Tenant Code": "TENANT_EXAMPLE", Host: "example.com", Primary: "Yes" },
            { "Tenant Code": "TENANT_EXAMPLE", Host: "www.example.com", Primary: "No" },
          ],
        },
      ],
      "tenant-import-template.xlsx",
    );
  }

  async function onFile(file: File) {
    try {
      const wb = read(await file.arrayBuffer(), { type: "array" });
      const tenantSheetName =
        wb.SheetNames.find((n) => n.trim().toLowerCase() === "tenants") ?? wb.SheetNames[0]!;
      const sheet = wb.Sheets[tenantSheetName]!;
      const json = utils.sheet_to_json<Record<string, unknown>>(sheet, {
        defval: "",
        raw: false,
      });

      // Optional "Tenant Domains" worksheet: one row per domain.
      const domainSheetName = wb.SheetNames.find(
        (n) => n.trim().toLowerCase() === "tenant domains",
      );
      const domainsByCode = new Map<string, Array<{ host: string; is_primary: boolean }>>();
      if (domainSheetName) {
        const drows = utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[domainSheetName]!, {
          defval: "",
          raw: false,
        });
        for (const r of drows) {
          const code = String(r["Tenant Code"] ?? "").trim().toUpperCase();
          const host = String(r["Host"] ?? r["Domain"] ?? "").trim().toLowerCase();
          if (!code || !host) continue;
          const flag = String(r["Primary"] ?? r["Is Primary"] ?? "").trim().toLowerCase();
          const list = domainsByCode.get(code) ?? [];
          list.push({ host, is_primary: flag === "yes" || flag === "true" || flag === "1" });
          domainsByCode.set(code, list);
        }
      }

      const seenCodes = new Set(tenants.map((t) => t.code.toUpperCase()));
      // Hosts already assigned to existing tenants must never be reassigned.
      const existingDetails = await Promise.all(
        tenants.map((t) => detailFn({ data: { tenantId: t.id } }).catch(() => null)),
      );
      const seenHosts = new Set<string>(
        existingDetails.flatMap((d) => (d?.domains ?? []).map((dm) => dm.host.toLowerCase())),
      );
      const rows: ImportRow[] = json.map((r, i) => {
        const name = String(r["Tenant Name"] ?? "").trim();
        const code = String(r["Tenant Code"] ?? "").trim().toUpperCase();
        const status = (String(r["Status"] ?? "pending").trim().toLowerCase() || "pending");
        const primary = String(r["Primary Domain"] ?? "").trim().toLowerCase();
        const extra = String(r["Additional Domains"] ?? "")
          .split(/[;,]/)
          .map((h) => h.trim().toLowerCase())
          .filter(Boolean);
        const sheetDomains = domainsByCode.get(code) ?? [];
        const domains = sheetDomains.length
          ? sheetDomains
          : [
              ...(primary ? [{ host: primary, is_primary: true }] : []),
              ...extra.map((h) => ({ host: h, is_primary: false })),
            ];
        if (domains.length > 0 && !domains.some((d) => d.is_primary)) {
          domains[0]!.is_primary = true;
        }
        let problem: string | null = null;
        if (!name) problem = "Tenant Name is required";
        else if (!/^[A-Z0-9_]{3,32}$/.test(code))
          problem = "Tenant Code must be 3–32 chars: A–Z, 0–9, underscore";
        else if (seenCodes.has(code)) problem = "Duplicate tenant code";
        else if (!(STATUSES as readonly string[]).includes(status))
          problem = "Status must be active, inactive or pending";
        else {
          for (const d of domains) {
            if (!isValidHost(d.host)) {
              problem = `Invalid domain: ${d.host}`;
              break;
            }
            if (seenHosts.has(d.host)) {
              problem = `Domain already assigned: ${d.host}`;
              break;
            }
          }
          if (!problem && domains.filter((d) => d.is_primary).length > 1) {
            problem = "Only one domain can be primary";
          }
        }
        if (!problem) {
          seenCodes.add(code);
          domains.forEach((d) => seenHosts.add(d.host));
        }
        return { row: i + 2, name, code, status, domains, problem };
      });
      if (rows.length === 0) {
        toast.error("The file has no rows.");
        return;
      }
      setImportRows(rows);
    } catch {
      toast.error("Could not read that file. Please use the provided template.");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function commitImport() {
    if (!importRows) return;
    setBusy(true);
    let imported = 0;
    const failed: Array<{ row: number; reason: string }> = [];
    for (const r of importRows) {
      if (r.problem) continue;
      try {
        await createFn({
          data: {
            name: r.name,
            code: r.code,
            status: r.status,
            domains: r.domains,
          },
        });
        imported += 1;
      } catch (err) {
        failed.push({ row: r.row, reason: friendlyError(err) });
      }
    }
    setBusy(false);
    setImportRows(null);
    await refresh();
    const skipped = importRows.filter((r) => r.problem).length;
    toast.success(
      `Imported ${imported}, skipped ${skipped}, failed ${failed.length}.`,
    );
    failed.forEach((f) => toast.error(`Row ${f.row}: ${f.reason}`));
  }

  if (accessQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Checking access…</p>;
  }

  if (accessQuery.data?.signedOut) {
    return (
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="h-4 w-4" aria-hidden />
            Session expired
          </CardTitle>
          <CardDescription>
            Your admin session is no longer valid. Please sign in again to open
            Tenant Management.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild size="sm">
            <a href="/admin/login">Go to admin login</a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!isOwner) {
    return (
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="h-4 w-4" aria-hidden />
            Not authorized
          </CardTitle>
          <CardDescription>
            Tenant Management is restricted to the platform owner. Your account
            does not have platform-level access.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Building2 className="h-6 w-6" aria-hidden />
            Tenant Management
          </h1>
          <p className="text-sm text-muted-foreground">
            Platform-level view and administration of tenants, members and
            domains.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap lg:justify-end">
          <Button size="sm" variant="outline" className="gap-1.5" disabled={busy} onClick={exportExcel}>
            <FileSpreadsheet className="h-3.5 w-3.5" aria-hidden />
            Export Excel
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={downloadTemplate}>
            <Download className="h-3.5 w-3.5" aria-hidden />
            Download Template
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5" aria-hidden />
            Import Excel
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onFile(f);
            }}
          />
          <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add Tenant
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tenants</CardTitle>
          <CardDescription>
            {tenants.length} tenant(s) on this platform.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {tenantsQuery.isError ? (
            <p className="text-sm text-destructive">
              {friendlyError(tenantsQuery.error)}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead>Domains</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenantsQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-muted-foreground">
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : (
                  tenants.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell className="font-medium">{t.name}</TableCell>
                      <TableCell className="font-mono text-xs">{t.code}</TableCell>
                      <TableCell>
                        <Badge variant={t.status === "active" ? "default" : "secondary"}>
                          {t.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{t.member_count}</TableCell>
                      <TableCell>{t.domain_count}</TableCell>
                      <TableCell className="text-xs">{fmt(t.created_at)}</TableCell>
                      <TableCell className="text-xs">{fmt(t.updated_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setViewId(t.id)}>
                            View
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditTenant(t)}>
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant={t.status === "active" ? "outline" : "secondary"}
                            className={
                              t.status === "active"
                                ? "border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                : undefined
                            }
                            onClick={() => setStatusTarget(t)}
                          >
                            {t.status === "active" ? "Deactivate" : "Activate"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Tenant deletion is intentionally not available — removing a tenant
            would affect its business data.
          </p>
        </CardContent>
      </Card>

      {/* View tenant */}
      <Dialog open={!!viewId} onOpenChange={(o) => !o && setViewId(null)}>
        <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-2">
              {detailQuery.data?.tenant.name ?? "Tenant details"}
              {detailQuery.data ? (
                <Badge
                  variant={
                    detailQuery.data.tenant.status === "active" ? "default" : "secondary"
                  }
                >
                  {detailQuery.data.tenant.status}
                </Badge>
              ) : null}
            </DialogTitle>
            <DialogDescription>
              Platform-level tenant information, members and domains.
            </DialogDescription>
          </DialogHeader>
          {detailQuery.isError ? (
            <p className="text-sm text-destructive">{friendlyError(detailQuery.error)}</p>
          ) : detailQuery.isLoading || !detailQuery.data ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <TenantDetailBody
              detail={detailQuery.data}
              onEdit={() => {
                const row = tenants.find((t) => t.id === viewId);
                if (row) {
                  setViewId(null);
                  setEditTenant(row);
                }
              }}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add tenant */}
      <TenantFormDialog
        key={addOpen ? "add-open" : "add-closed"}
        open={addOpen}
        title="Add Tenant"
        description="New tenants are created in a pending state and are not activated automatically."
        initial={{ name: "", code: "", status: "pending", domains: [], emails: [] }}
        busy={busy}
        onCancel={() => setAddOpen(false)}
        onSubmit={async (v) => {
          setBusy(true);
          try {
            const created = await createFn({
              data: {
                name: v.name,
                code: v.code,
                status: v.status,
                domains: v.domains ?? [],
              },
            });
            if ((v.emails ?? []).length > 0 && created?.id) {
              await emailsFn({
                data: { tenantId: created.id, emails: v.emails ?? [] },
              });
            }
            setAddOpen(false);
            await refresh();
            toast.success("Tenant created.");
          } catch (err) {
            toast.error(friendlyError(err));
          } finally {
            setBusy(false);
          }
        }}
      />


      {/* Edit tenant */}
      <TenantFormDialog
        key={`${editTenant?.id ?? "edit-closed"}-${editDetailQuery.isSuccess && editEmailsQuery.isSuccess ? "ready" : "loading"}`}
        open={!!editTenant}
        title="Edit Tenant"
        description="Tenant name, code, status and domains can be changed. Business data is not affected."
        initial={{
          name: editTenant?.name ?? "",
          code: editTenant?.code ?? "",
          status: editTenant?.status ?? "pending",
          domains: (editDetailQuery.data?.domains ?? []).map((d) => ({
            host: d.host,
            is_primary: Boolean(d.is_primary),
          })),
          emails: editEmailsQuery.data ?? [],
        }}
        tenantId={editTenant?.id}
        busy={busy}
        domainsLoading={
          !!editTenant && (editDetailQuery.isLoading || editEmailsQuery.isLoading)
        }
        onCancel={() => setEditTenant(null)}
        onSubmit={async (v) => {
          if (!editTenant) return;
          setBusy(true);
          try {
            await updateFn({
              data: {
                tenantId: editTenant.id,
                name: v.name,
                code: v.code,
                status: v.status,
              },
            });
            await domainsFn({
              data: { tenantId: editTenant.id, domains: v.domains ?? [] },
            });
            await emailsFn({
              data: { tenantId: editTenant.id, emails: v.emails ?? [] },
            });
            setEditTenant(null);
            await refresh();
            await queryClient.invalidateQueries({ queryKey: ["platform", "tenant"] });
            await queryClient.invalidateQueries({
              queryKey: ["platform", "tenant-emails"],
            });
            toast.success("Tenant updated.");

          } catch (err) {
            toast.error(friendlyError(err));
          } finally {
            setBusy(false);
          }
        }}
      />


      {/* Activate / deactivate confirmation */}
      <AlertDialog
        open={!!statusTarget}
        onOpenChange={(o) => !o && setStatusTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusTarget?.status === "active" ? "Deactivate" : "Activate"}{" "}
              {statusTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This changes the tenant status only. No orders, customers,
              subscriptions, pricing, products, CMS, media or payments are
              modified or deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const target = statusTarget;
                setStatusTarget(null);
                if (!target) return;
                try {
                  await statusFn({
                    data: {
                      tenantId: target.id,
                      status: target.status === "active" ? "inactive" : "active",
                    },
                  });
                  await refresh();
                  toast.success("Tenant status updated.");
                } catch (err) {
                  toast.error(friendlyError(err));
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import preview */}
      <Dialog open={!!importRows} onOpenChange={(o) => !o && setImportRows(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import preview</DialogTitle>
            <DialogDescription>
              Only valid rows are imported. Existing tenants are never
              overwritten or deleted, and imported tenants are not activated.
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Row</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Domains</TableHead>
                <TableHead>Result</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(importRows ?? []).map((r) => (
                <TableRow key={r.row}>
                  <TableCell>{r.row}</TableCell>
                  <TableCell>{r.name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.code || "—"}</TableCell>
                  <TableCell>{r.status}</TableCell>
                  <TableCell className="text-xs">
                    {r.domains.map((d) => d.host).join(", ") || "—"}
                  </TableCell>
                  <TableCell className="text-xs">
                    {r.problem ? (
                      <span className="text-destructive">Skipped — {r.problem}</span>
                    ) : (
                      "Will import"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportRows(null)}>
              Cancel
            </Button>
            <Button
              disabled={busy || !(importRows ?? []).some((r) => !r.problem)}
              onClick={commitImport}
            >
              Import valid rows
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TenantDetailBody({
  detail,
  onEdit,
}: {
  detail: PlatformTenantDetail;
  onEdit?: () => void;
}) {
  const sortedDomains = [...detail.domains].sort(
    (a, b) => Number(b.is_primary) - Number(a.is_primary),
  );
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 p-4">
        <dl className="grid flex-1 gap-x-8 gap-y-4 sm:grid-cols-2">
          <Field label="Tenant ID" value={detail.tenant.id} mono />
          <Field label="Tenant Code" value={detail.tenant.code} mono />
          <Field label="Tenant Name" value={detail.tenant.name} />
          <Field label="Status" value={detail.tenant.status} />
          <Field label="Created" value={fmt(detail.tenant.created_at)} />
          <Field label="Updated" value={fmt(detail.tenant.updated_at)} />
        </dl>
        {onEdit ? (
          <Button size="sm" variant="outline" onClick={onEdit} className="gap-2">
            <PenSquare className="h-4 w-4" aria-hidden />
            Edit Tenant
          </Button>
        ) : null}
      </div>


      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users">Users ({detail.users.length})</TabsTrigger>
          <TabsTrigger value="domains">Domains ({detail.domains.length})</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Member since</TableHead>
                  <TableHead>Last sign-in</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {detail.users.map((u) => (
                  <TableRow key={u.user_id}>
                    <TableCell className="text-sm font-medium break-all">
                      {u.email ?? u.user_id}
                    </TableCell>
                    <TableCell className="text-xs">
                      {u.roles.length ? u.roles.join(", ") : "—"}
                    </TableCell>
                    <TableCell className="text-xs">{fmt(u.joined_at)}</TableCell>
                    <TableCell className="text-xs">{fmt(u.last_sign_in_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Showing {detail.users.length} of {detail.users.length} users
          </p>
        </TabsContent>

        <TabsContent value="domains" className="mt-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">Domains ({detail.domains.length})</h2>
            {onEdit ? (
              <Button size="sm" variant="outline" onClick={onEdit}>
                Manage Domains
              </Button>
            ) : null}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Host</TableHead>
                  <TableHead>Primary</TableHead>
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedDomains.map((d) => (
                  <TableRow key={d.host}>
                    <TableCell className="font-mono text-xs break-all">{d.host}</TableCell>
                    <TableCell>
                      {d.is_primary ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                          <Check className="h-3.5 w-3.5" aria-hidden />
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{fmt(d.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Showing {detail.domains.length} of {detail.domains.length} domains
          </p>
        </TabsContent>

        <TabsContent value="summary" className="mt-4">
          <dl className="grid gap-3 sm:grid-cols-2">
            <Field label="Status" value={detail.tenant.status} />
            <Field label="Users" value={String(detail.users.length)} />
            <Field label="Domains" value={String(detail.domains.length)} />
            <Field
              label="Primary domain"
              value={sortedDomains.find((d) => d.is_primary)?.host ?? "—"}
            />
            <Field label="Created" value={fmt(detail.tenant.created_at)} />
            <Field label="Updated" value={fmt(detail.tenant.updated_at)} />
          </dl>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export type DomainEntry = { host: string; is_primary: boolean };

type FormValue = {
  name: string;
  code: string;
  status: string;
  domains?: DomainEntry[];
  emails?: string[];
};


const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

function isValidHost(host: string) {
  return host === "localhost" || DOMAIN_RE.test(host);
}

/** Multi-domain editor. Exactly one primary while at least one domain exists. */
function DomainsEditor({
  value,
  onChange,
  disabled,
}: {
  value: DomainEntry[];
  onChange: (next: DomainEntry[]) => void;
  disabled?: boolean;
}) {
  const [removeIdx, setRemoveIdx] = useState<number | null>(null);

  const setHost = (i: number, host: string) =>
    onChange(value.map((d, idx) => (idx === i ? { ...d, host } : d)));

  const setPrimary = (i: number) =>
    onChange(value.map((d, idx) => ({ ...d, is_primary: idx === i })));

  const add = () =>
    onChange([...value, { host: "", is_primary: value.length === 0 }]);

  const remove = (i: number) => {
    const next = value.filter((_, idx) => idx !== i);
    if (next.length > 0 && !next.some((d) => d.is_primary)) next[0]!.is_primary = true;
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <Label>Domains</Label>
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No domains. A tenant can be saved without a domain.
        </p>
      ) : (
        <div className="space-y-2">
          {value.map((d, i) => (
            <div key={i} className="flex items-center gap-2">
              <label className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                <input
                  type="radio"
                  name="tenant-primary-domain"
                  checked={d.is_primary}
                  disabled={disabled}
                  onChange={() => setPrimary(i)}
                  aria-label={`Make ${d.host || "this domain"} primary`}
                />
                Primary
              </label>
              <Input
                value={d.host}
                disabled={disabled}
                placeholder="example.com"
                onChange={(e) => setHost(i, e.target.value.trim().toLowerCase())}
              />
              <Button
                size="sm"
                variant="outline"
                type="button"
                disabled={disabled}
                onClick={() => setRemoveIdx(i)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button size="sm" variant="outline" type="button" disabled={disabled} onClick={add}>
        <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
        Add Domain
      </Button>

      <AlertDialog open={removeIdx !== null} onOpenChange={(o) => !o && setRemoveIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this domain?</AlertDialogTitle>
            <AlertDialogDescription>
              The domain will no longer resolve to this tenant. No tenant
              business data is deleted or modified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removeIdx !== null) remove(removeIdx);
                setRemoveIdx(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const EMAIL_RE =
  /^[^@\s]+@[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

/** Multi-email editor. Mirrors the domains editor. Emails are optional. */
function EmailsEditor({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [removeIdx, setRemoveIdx] = useState<number | null>(null);

  const setEmail = (i: number, email: string) =>
    onChange(value.map((e, idx) => (idx === i ? email : e)));

  return (
    <div className="space-y-2">
      <Label>Emails</Label>
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No emails. A tenant can be saved without an email.
        </p>
      ) : (
        <div className="space-y-2">
          {value.map((e, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={e}
                disabled={disabled}
                placeholder="email@example.com"
                onChange={(ev) => setEmail(i, ev.target.value.trim().toLowerCase())}
              />
              <Button
                size="sm"
                variant="outline"
                type="button"
                disabled={disabled}
                onClick={() => setRemoveIdx(i)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button
        size="sm"
        variant="outline"
        type="button"
        disabled={disabled}
        onClick={() => onChange([...value, ""])}
      >
        <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
        Add Email
      </Button>

      <AlertDialog open={removeIdx !== null} onOpenChange={(o) => !o && setRemoveIdx(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this email?</AlertDialogTitle>
            <AlertDialogDescription>
              The email will no longer be associated with this tenant. No tenant
              business data, user account or role is deleted or modified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removeIdx !== null)
                  onChange(value.filter((_, idx) => idx !== removeIdx));
                setRemoveIdx(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}



function TenantFormDialog({
  open,
  title,
  description,
  initial,
  tenantId,
  busy,
  domainsLoading,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  title: string;
  description: string;
  initial: FormValue;
  tenantId?: string;
  busy: boolean;
  domainsLoading?: boolean;
  onCancel: () => void;
  onSubmit: (value: FormValue) => void | Promise<void>;
}) {
  const [name, setName] = useState(initial.name);
  const [code, setCode] = useState(initial.code);
  const [status, setStatus] = useState(initial.status);
  const [domains, setDomains] = useState<DomainEntry[]>(initial.domains ?? []);
  const [emails, setEmails] = useState<string[]>(initial.emails ?? []);
  const [error, setError] = useState<string | null>(null);


  const submit = () => {
    const cleanName = name.trim();
    const cleanCode = code.trim().toUpperCase();
    if (!cleanName) return setError("Tenant name is required.");
    if (!/^[A-Z0-9_]{3,32}$/.test(cleanCode))
      return setError("Tenant code must be 3–32 characters: A–Z, 0–9 or underscore.");

    const cleaned = domains.map((d) => ({
      host: d.host.trim().toLowerCase(),
      is_primary: d.is_primary,
    }));
    if (cleaned.some((d) => !d.host)) return setError("Domain is required.");
    const bad = cleaned.find((d) => !isValidHost(d.host));
    if (bad) return setError(`Invalid domain format: ${bad.host}`);
    const seen = new Set<string>();
    for (const d of cleaned) {
      if (seen.has(d.host)) return setError(`Duplicate domain: ${d.host}`);
      seen.add(d.host);
    }
    if (cleaned.length > 0 && cleaned.filter((d) => d.is_primary).length !== 1)
      return setError("Exactly one domain must be marked as primary.");

    const cleanEmails = emails.map((e) => e.trim().toLowerCase());
    if (cleanEmails.some((e) => !e)) return setError("Email address is required.");
    const badEmail = cleanEmails.find((e) => !EMAIL_RE.test(e));
    if (badEmail) return setError(`Invalid email format: ${badEmail}`);
    const seenEmails = new Set<string>();
    for (const e of cleanEmails) {
      if (seenEmails.has(e)) return setError(`Duplicate email: ${e}`);
      seenEmails.add(e);
    }

    setError(null);
    void onSubmit({
      name: cleanName,
      code: cleanCode,
      status,
      domains: cleaned,
      emails: cleanEmails,
    });

  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {tenantId ? (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Tenant ID
              </Label>
              <p className="font-mono text-xs">{tenantId}</p>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="tenant-name">Tenant Name</Label>
            <Input id="tenant-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tenant-code">Tenant Code</Label>
            <Input
              id="tenant-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="TENANT_EXAMPLE"
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              3–32 characters, A–Z, 0–9 and underscore. Must be unique.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {domainsLoading ? (
            <p className="text-sm text-muted-foreground">Loading domains…</p>
          ) : (
            <>
              <DomainsEditor value={domains} onChange={setDomains} disabled={busy} />
              <EmailsEditor value={emails} onChange={setEmails} disabled={busy} />
            </>
          )}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || domainsLoading}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className={mono ? "font-mono text-xs" : "text-sm"}>{value}</dd>
    </div>
  );
}
